import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, QrCode, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminTables() {
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", capacity: 4 });
  const [qrTable, setQrTable] = useState(null);

  const load = () => api.get("/admin/tables").then((r) => setTables(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => { try { await api.post("/admin/tables", form); toast.success("Table added"); setOpen(false); setForm({ label: "", capacity: 4 }); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/tables/${id}`); load(); };
  const toggleActive = async (t) => {
    try {
      await api.patch(`/admin/tables/${t.id}`, { label: t.label, capacity: t.capacity, is_active: !t.is_active });
      toast.success(`${t.label} ${t.is_active ? "deactivated — QR paused" : "activated"}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const menuUrl = (token) => `${window.location.origin}/m/${token}`;
  // Fetch the QR through the axios client — it carries BOTH auth headers
  // (Bearer + X-Restaurant-Id); a plain <img src> drops them, which broke the
  // image for super-admin god-mode and made Download 401 for everyone.
  const downloadQr = async (t) => {
    try {
      const r = await api.get(`/admin/tables/${t.id}/qr.png`, {
        params: { base_url: window.location.origin }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${t.label.replace(/\s+/g, "-")}-qr.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download QR"); }
  };

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">Physical Layout</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Tables & QR Codes</h1>
        </div>
        <Button data-testid="tables-add-btn" onClick={() => setOpen(true)} className="rounded-lg bg-[var(--qd-charcoal)] hover:bg-black"><Plus className="w-4 h-4 mr-1" /> Add Table</Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tables.map((t) => (
          <div key={t.id} data-testid={`table-card-${t.id}`} className={`qd-admin-card p-5 ${t.is_active ? "" : "opacity-60"}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display font-bold text-lg">{t.label}</div>
                <div className="text-xs text-gray-400">Capacity: {t.capacity}</div>
              </div>
              <button data-testid={`table-qr-${t.id}`} onClick={() => setQrTable(t)} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"><QrCode className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-gray-500 truncate">{menuUrl(t.token)}</div>
            <div className="mt-3 flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5" title={t.is_active ? "Deactivate — QR stops working" : "Activate — QR works again"}>
                <Switch data-testid={`table-active-${t.id}`} checked={!!t.is_active} onCheckedChange={() => toggleActive(t)} />
                <span className={`text-xs font-semibold ${t.is_active ? "text-green-700" : "text-gray-400"}`}>{t.is_active ? "Active" : "Inactive"}</span>
              </label>
              <a href={menuUrl(t.token)} target="_blank" rel="noreferrer" data-testid={`table-open-${t.id}`} className="text-xs inline-flex items-center gap-1 text-gray-600 hover:text-black ml-auto"><ExternalLink className="w-3 h-3" /> Open</a>
              <button data-testid={`table-delete-${t.id}`} onClick={() => del(t.id)} className="text-xs inline-flex items-center gap-1 text-gray-600 hover:text-red-600"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New Table</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="table-label" placeholder="Label (e.g. Table 7)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <Input data-testid="table-capacity" type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value || "4") })} />
            <Button data-testid="table-save" onClick={save} className="w-full bg-[var(--qd-charcoal)] hover:bg-black">Add</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrTable} onOpenChange={(o) => !o && setQrTable(null)}>
        <DialogContent data-testid="qr-modal">
          <DialogHeader><DialogTitle>{qrTable?.label} QR Code</DialogTitle></DialogHeader>
          {qrTable && (
            <div className="text-center space-y-3">
              <QRImage tableId={qrTable.id} />
              <a href={menuUrl(qrTable.token)} className="text-xs text-gray-500 break-all block">{menuUrl(qrTable.token)}</a>
              <div className="flex gap-2 justify-center">
                <Button data-testid="qr-download" onClick={() => downloadQr(qrTable)} variant="outline"><Download className="w-4 h-4 mr-2" /> Download</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QRImage({ tableId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    // axios client adds Bearer + X-Restaurant-Id — required in god-mode.
    api.get(`/admin/tables/${tableId}/qr.png`, { params: { base_url: window.location.origin }, responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc("error"));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [tableId]);
  if (src === "error") return <div className="w-64 h-64 mx-auto grid place-items-center bg-gray-50 rounded text-xs text-gray-400">Could not load QR</div>;
  if (!src) return <div className="w-64 h-64 mx-auto bg-gray-100 animate-pulse rounded" />;
  return <img src={src} alt="QR" className="w-64 h-64 mx-auto" data-testid="qr-image" />;
}
